import { useCallback, useEffect, useState } from 'react';
import APIClient from '../api/api';
import { FastTransferCaptureResponse, FastTransferJobDetail } from '../api/models';
import { Button, StatusTag } from './ui';
import {
  captureOutcomeMessage,
  hasApprovedTerms,
  hasValidatedAuth,
  isActive,
  stateLabel,
  stateTone,
} from './DomainFastTransfer.model';

export interface Props {
  domain: string;
}

type Busy = null | 'loading' | 'activating' | 'capturing' | 'accepting' | 'approving';

const DomainFastTransfer = ({ domain }: Props) => {
  const [detail, setDetail] = useState<FastTransferJobDetail | null>(null);
  const [busy, setBusy] = useState<Busy>('loading');
  const [error, setError] = useState<string | null>(null);
  const [capture, setCapture] = useState<FastTransferCaptureResponse | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await APIClient.getFastTransferJob(domain);
      setDetail(d);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to load the fast transfer status.');
    } finally {
      setBusy(null);
    }
  }, [domain]);

  useEffect(() => {
    setBusy('loading');
    load();
  }, [load]);

  function fail(e: any, fallback: string) {
    setError(e?.response?.data?.error || fallback);
  }

  async function activate() {
    setBusy('activating');
    setError(null);
    setNotice(null);
    try {
      const res = await APIClient.activateFastTransfer(domain);
      setNotice(activateNotice(res.status, res.emailAction));
      await load();
    } catch (e: any) {
      fail(e, 'Failed to activate fast transfer.');
    } finally {
      setBusy(null);
    }
  }

  async function runCapture(jobId: number) {
    setBusy('capturing');
    setError(null);
    setNotice(null);
    setCapture(null);
    try {
      const res = await APIClient.captureFastTransferTerms(jobId);
      setCapture(res);
      setNotice(captureOutcomeMessage(res.outcome, res.error));
      await load();
    } catch (e: any) {
      fail(e, 'Failed to capture the authorization page.');
    } finally {
      setBusy(null);
    }
  }

  async function approve(versionId: number) {
    setBusy('approving');
    setError(null);
    try {
      await APIClient.approveFastTransferTerms(versionId);
      await load();
    } catch (e: any) {
      fail(e, 'Failed to approve the terms version.');
    } finally {
      setBusy(null);
    }
  }

  async function accept(jobId: number) {
    setBusy('accepting');
    setError(null);
    setNotice(null);
    try {
      await APIClient.acceptFastTransferTerms(jobId);
      setNotice('Ran the clicker. The status below reflects the outcome.');
      await load();
    } catch (e: any) {
      fail(e, 'Failed to accept the terms.');
    } finally {
      setBusy(null);
    }
  }

  const job = detail?.job ?? null;
  const terms = detail?.termsVersions ?? [];
  const anyBusy = busy !== null;
  const termsApproved = hasApprovedTerms(terms);

  return (
    <div className="flex flex-col gap-4">
      <div className="text-[12.5px] text-text-2 max-w-[640px]">
        Activates the Afternic fast transfer for this domain. Activation adopts the job so
        the registrar's authorization email correlates, then the clicker accepts the terms.
        Every gate still applies — account scope, the clicker kill switch, and an approved
        terms version — so nothing is accepted until you have reviewed the terms.
      </div>

      {error && (
        <div className="text-[12.5px] text-danger" role="alert">
          {error}
        </div>
      )}
      {notice && (
        <div className="text-[12.5px] text-text-2" role="status">
          {notice}
        </div>
      )}

      {busy === 'loading' ? (
        <div className="text-[12.5px] text-text-3">Loading…</div>
      ) : (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider text-text-3 font-semibold">
              State
            </span>
            {job ? (
              <StatusTag tone={stateTone(job.state)}>{stateLabel(job.state)}</StatusTag>
            ) : (
              <StatusTag tone="neutral">No job yet</StatusTag>
            )}
            {job?.resubmitSource === 'admin' && (
              <span className="text-[11px] text-text-3">admin-initiated</span>
            )}
          </div>

          {detail?.email && (
            <div className="text-[11.5px] text-text-3">
              Matched authorization email: <span className="text-text-2">{detail.email.subject}</span>{' '}
              from {detail.email.from}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              variant="primary"
              size="sm"
              loading={busy === 'activating'}
              disabled={anyBusy || isActive(job)}
              onClick={activate}
            >
              {job ? 'Re-check activation' : 'Activate fast transfer'}
            </Button>
            <Button
              variant="subtle"
              size="sm"
              loading={busy === 'capturing'}
              disabled={anyBusy || !hasValidatedAuth(job)}
              onClick={() => job && runCapture(job.id)}
              title={hasValidatedAuth(job) ? undefined : 'Needs a validated authorization email first'}
            >
              Capture terms page
            </Button>
            <Button
              variant="subtle"
              size="sm"
              loading={busy === 'accepting'}
              disabled={anyBusy || !hasValidatedAuth(job) || !termsApproved}
              onClick={() => job && accept(job.id)}
              title={termsApproved ? undefined : 'Approve a terms version first'}
            >
              Accept terms
            </Button>
          </div>

          {capture?.termsText && (
            <div className="flex flex-col gap-2 bg-panel border border-border rounded-md px-4 py-3">
              <div className="text-[10px] uppercase tracking-wider text-text-3 font-semibold">
                Captured terms
              </div>
              <div className="font-mono text-[11px] text-text-2 max-h-40 overflow-auto whitespace-pre-wrap">
                {capture.termsText}
              </div>
            </div>
          )}

          {terms.length > 0 && (
            <div className="flex flex-col gap-2">
              <div className="text-[10px] uppercase tracking-wider text-text-3 font-semibold">
                Terms versions
              </div>
              {terms.map((v) => (
                <div
                  key={v.id}
                  className="flex items-center gap-2 flex-wrap bg-panel border border-border rounded-md px-3 py-2"
                >
                  <span className="font-mono text-[11px] text-text-2 break-all">{v.termsHash}</span>
                  {v.active && v.approvedAt ? (
                    <StatusTag tone="success">approved</StatusTag>
                  ) : (
                    <>
                      <StatusTag tone="warn">unapproved</StatusTag>
                      <Button
                        variant="subtle"
                        size="sm"
                        loading={busy === 'approving'}
                        disabled={anyBusy}
                        onClick={() => approve(v.id)}
                      >
                        Approve
                      </Button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

function activateNotice(status: string, emailAction?: string): string {
  if (status === 'awaiting_email') {
    return 'Activated. Waiting for the registrar authorization email to arrive at the parser address.';
  }
  if (status.startsWith('auth_validated') || emailAction === 'fast_transfer_clicker_dispatched') {
    return 'Activated and the authorization email was matched and validated.';
  }
  if (status === 'fast_transfer_active') {
    return 'This domain is already opted in.';
  }
  return `Activated (${status}).`;
}

export default DomainFastTransfer;
